/**
 * Is the EDC terminal still holding a live transaction?
 *
 * WHY THIS MATTERS. The Permata ECR protocol has NO cancel message. Once a
 * transaction is armed, nothing we send can recall it — the terminal keeps the
 * card slot open, or keeps the QR on screen, until the customer acts or its own
 * window closes. The panel already reflects that by withholding a cancel button
 * in these phases: "a button here could not do what it says."
 *
 * But hiding a button is not the same as closing the exits. Radix dismisses a
 * dialog on Escape and on an outside click by default, and neither was
 * prevented — so a cashier could dismiss the screen while the terminal was
 * still waiting for payment. Reported from a counter: the POS said cancelled,
 * the terminal was still showing the QR.
 *
 * Nothing server-side stops either: the transaction stays armed, the poller
 * keeps running, and `/edc` deliberately has no cancel-on-disconnect (on a
 * counter with an e-money reader too, that would cancel the WRONG device). So
 * if the customer then pays, the sale settles and mints a wristband that the
 * cashier believes was cancelled and will not hand over.
 *
 * Hence this predicate: while it is true the dialog must stay put. The
 * transaction is cancelled AT THE TERMINAL, or not at all.
 */

import type { EdcPhase } from '@/types/payment';

/**
 * Phases where the terminal is armed and the customer can still pay.
 *
 * `idle` is before we have armed anything. Everything from `approved` onward is
 * an outcome the terminal has already reported, including `unresolved` — which
 * is silence, not a live transaction, and whose own screen is the resolution
 * prompt rather than a waiting state.
 */
const LIVE_PHASES: ReadonlySet<EdcPhase> = new Set<EdcPhase>([
  'connecting',
  'waiting_card',
  'processing',
]);

export function isTerminalLive(phase?: EdcPhase | null): boolean {
  return phase != null && LIVE_PHASES.has(phase);
}

export const _test = { LIVE_PHASES };
